import type { PropsWithStyles } from '@morpho-org/ui-v6/types';
import { ChainUtils, MathLib } from '@morpho-org/blue-sdk';
import { ZERO } from '@morpho-org/dapp-core';
import { Avatar } from '@morpho-org/ui-v6/client';
import { Donut, ExternalLink, getDonutColor } from '@morpho-org/ui-v6/server';
import { cn, formatters } from '@morpho-org/ui-v6/utils';
import { Badge } from '@repo/ui';
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { formatUnits } from 'viem';
import { getShareFromTotal } from '@/helpers/vault';
import type { CollateralExposure } from '../CollateralExposureStack';
const PERCENT_FACTOR = 100n;
export interface HoverCardContentProps extends PropsWithStyles {
  collateralExposures: CollateralExposure[];
  totalAssetsUsd: bigint | null | undefined;
}
const HoverCardContent = ({
  className,
  collateralExposures,
  totalAssetsUsd
}: HoverCardContentProps) => {
  let percentageOffset: bigint = 0n;
  const rows = collateralExposures.map((exposure, index) => {
    const supplyAssetsUsd = exposure.supplyAssetsUsd;
    const isShareKnown = supplyAssetsUsd !== null && totalAssetsUsd != null;
    const percentShare = isShareKnown ? getShareFromTotal(totalAssetsUsd, supplyAssetsUsd) : ZERO;
    const percentShareNumber = Number(formatUnits(percentShare, 18)) * 100;
    const formattedAllocation = supplyAssetsUsd === null ? '-' : formatters.short.digits(2).unit('$').of(supplyAssetsUsd, 18);
    const formattedPercentage = !isShareKnown ? '-' : formatters.percent.default('0').trailingZero(false).digits(2).unit('%').of(percentShare, 18);
    const formattedPercentageOffset = Number(percentageOffset * PERCENT_FACTOR / MathLib.WAD);
    const donutColor = getDonutColor(index);
    const explorerUrl = ChainUtils.getExplorerAddressUrl(exposure.token.chain.id, exposure.token.address);
    const row = {
      exposure,
      percentShareNumber,
      formattedAllocation,
      formattedPercentage,
      formattedPercentageOffset,
      donutColor,
      explorerUrl
    };
    percentageOffset += percentShare;
    return row;
  });
  return <div className={cn('overflow-hidden', className)}>
      <div className="p-sm max-h-[300px] overflow-auto">
        <div className="flex flex-col">
          {rows.map(row_0 => <ExternalLink key={row_0.exposure.token.address} href={row_0.explorerUrl} className="gap-md py-2xs px-xs hover:bg-background flex items-center justify-between rounded-lg text-inherit">
              <div className="gap-2xs flex min-w-[0px] flex-[1px] items-center">
                <Avatar src={row_0.exposure.token.logoURI} size="small" />
                <span className="text-2xs text-foreground min-w-0 truncate">
                  {row_0.exposure.token.symbol || '-'}
                </span>
                {row_0.exposure.isIdle && <Badge variant="tertiary" size="sm">
                    Idle
                  </Badge>}
              </div>
              <div className="gap-xs flex items-center">
                <span className="text-2xs text-foreground" style={{
              whiteSpace: 'nowrap'
            }}>
                  {row_0.formattedAllocation}
                </span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex cursor-default">
                      <Donut allocation={row_0.percentShareNumber} color={row_0.donutColor} backgroundColor="var(--color-muted)" percentageOffset={row_0.formattedPercentageOffset} />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>{`Vault allocation: ${row_0.formattedPercentage}`}</TooltipContent>
                </Tooltip>
              </div>
            </ExternalLink>)}
        </div>
      </div>
    </div>;
};
export default HoverCardContent;